import { avatarDefault } from '@/assets/images';
import { Divider, Stack } from '@/components/layout';
import { Level, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { PlayerCardAvatar } from '@/features/player/components/list-player/player-card/player-card-avatar';
import { PlayerCardDesc } from '@/features/player/components/list-player/player-card/player-card-desc';
import { PlayerCardIndicator } from '@/features/player/components/list-player/player-card/player-card-root';
import { PlayerCardTitle } from '@/features/player/components/list-player/player-card/player-card-title';
import { getAvatarCacheBuster } from '@/features/player/utils/avatar-cache-buster';
import { buildPlayerAvatarSource } from '@/features/player/utils/avatar-uri';
import { useScaling } from '@/hooks';
import { PlayerStackParamList } from '@/navigations/types';
import { colors, ColorToken, withOpacity } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { memo } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';

export const SelectPlayerCard = memo(
  ({
    player,
    checked,
    onToggleChecked,
  }: {
    player: player.PlayerLookupData;
    checked: boolean;
    onToggleChecked: (playerId: string) => void;
  }) => {
    const { scale, isTablet } = useScaling();
    const navigation = useNavigation<NativeStackNavigationProp<PlayerStackParamList>>();
    const avatarSize = scale(48);
    const avatarSource =
      buildPlayerAvatarSource({
        playerId: player.id,
        avatarUrl: player.avatarUrl ?? player.profilePicture,
      }) ?? avatarDefault;

    const onPressProfile = () => {
      navigation.navigate('PlayerProfile', {
        playerId: player.id,
        mutateDeleteReturnTo: 'SelectPlayer',
      });
    };

    return (
      <Stack paddingX={scale({ phone: 18, tablet: 0 })}>
        <Stack
          direction="row"
          align="stretch"
          height={scale(70)}
          backgroundColor="white"
          borderRadius={scale(4)}
          overflow="hidden"
          style={styles.shadow}>
          <PlayerCardIndicator color={colors[`Level ${player.level}` as ColorToken]} />
          <Stack
            direction="row"
            align="center"
            height="100%"
            paddingLeft={scale(16)}
            paddingRight={scale(12)}
            gap={scale(12)}
            flex={1}>
            <PlayerCardAvatar size={avatarSize} source={avatarSource} onPress={onPressProfile} />

            <Divider
              orientation="vertical"
              thickness={isTablet ? 1 : StyleSheet.hairlineWidth}
              color={colors['6']}
              spacing={scale(0)}
            />

            <Pressable
              onPress={() => onToggleChecked(player.id)}
              style={styles.infoPressable}
              android_ripple={{ color: withOpacity(colors.Blue, 0.08) }}>
              <Stack flex={1} justify="center" gap={scale(8)}>
                <Stack gap={scale(2)}>
                  <PlayerCardTitle
                    title={player.name.replace(STRINGS.player.listPlayers.creatingPlayerMarker, '')}
                  />
                  <PlayerCardDesc>
                    {`${player.age ?? ''}`}{' '}
                    {player.age !== null && player.age !== undefined
                      ? player.age > 1
                        ? STRINGS.player.listPlayers.ageYearsOld
                        : STRINGS.player.listPlayers.ageYearOld
                      : ''}
                  </PlayerCardDesc>
                </Stack>

                <Level value={player.level} alignSelf="flex-start" />
              </Stack>
            </Pressable>
          </Stack>

          <Pressable
            onPress={() => onToggleChecked(player.id)}
            style={[
              styles.checkArea,
              {
                width: scale(80),
                backgroundColor: checked ? colors.Success : colors['Backgrounds/Primary'],
              },
            ]}
            android_ripple={{ color: withOpacity(colors['0'], 0.12) }}>
            <Stack align="center" justify="center" gap={scale(3)} height="100%">
              <SvgIcon name={checked ? 'IcChecked' : 'IcUnchecked'} size={scale(24)} />
              <Text
                fontWeight={'400'}
                fontSize={7}
                transform="uppercase"
                fontFamily="Inter"
                style={{ color: checked ? colors['0'] : colors['6'] }}>
                {checked ? '' : 'SELECT'}
              </Text>
            </Stack>
          </Pressable>
        </Stack>
      </Stack>
    );
  },
  (prev, next) => {
    const prevV = getAvatarCacheBuster(prev.player.id);
    const nextV = getAvatarCacheBuster(next.player.id);
    return (
      prev.checked === next.checked &&
      prev.player.id === next.player.id &&
      prev.player.name === next.player.name &&
      prev.player.age === next.player.age &&
      prev.player.level === next.player.level &&
      prev.player.avatarUrl === next.player.avatarUrl &&
      prev.player.profilePicture === next.player.profilePicture &&
      prevV === nextV
    );
  },
);

const styles = StyleSheet.create({
  shadow:
    Platform.OS === 'ios'
      ? {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.16,
          shadowRadius: 2,
        }
      : {
          elevation: 2,
        },
  checkArea: {},
  infoPressable: { flex: 1 },
});
